import Lenis from '@lib/lenis';
import { forceUnlockScrollIfIdle } from '@theme/coco-utilities';

const resizeSubscribers = new Set();

function onResize() {
  for (const callback of resizeSubscribers) {
    callback();
  }
}

const ResizeManager = {
  subscribe(callback, context = null) {
    resizeSubscribers.add(callback);
    if (context) {
      console.log('[ResizeManager] Subscribed:', context);
    }
  },
  unsubscribe(callback, context = null) {
    resizeSubscribers.delete(callback);
    if (context) {
      console.log('[ResizeManager] Unsubscribed:', context);
    }
  },
  triggerNow() {
    onResize();
  },
};

window.addEventListener('resize', onResize);

class LenisSmoothScroll {
  constructor() {
    // Global flags
    this.lenis = null;
    this.rafHandle = null;
    this._safeResizeTimer = null;
    this._contentResizeObserver = null;

    // Bind methods once
    this.onScroll = this.onScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.scheduleSafeResize = this.scheduleSafeResize.bind(this);
    this.isTouchDeviceManage = this.isTouchDevice();

    // Initial setup based on current state
    if (this.shouldEnable()) {
      this.init();
    }

    // Always listen to resize so we can toggle on/off when breakpoint changes
    ResizeManager.subscribe(this.handleResize);
  }

  // Touch detection
  isTouchDevice() {
    return window.matchMedia('(pointer: coarse)').matches;
  }

  // Viewport / device–based condition
  shouldEnable() {
    // Respect theme setting
    const smoothFlag = document.body.dataset.smoothScroll === 'true';
    if (!smoothFlag) return false;

    // Disable completely on touch devices
    if (this.isTouchDevice()) return false;

    // Optional: only enable on "desktop" widths
    // Adjust breakpoint if you want
    const DESKTOP_MIN_WIDTH = 768;
    if (window.innerWidth < DESKTOP_MIN_WIDTH) return false;

    return true;
  }

  init() {
    if (this.lenis) return; // already active, safety guard


    this.lenis = new Lenis({
      duration: 2.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: !this.isTouchDeviceManage, // Disable smooth scrolling on mobile and tablet
      mouseMultiplier: 1,
      smoothTouch: false, // Optional: Disable touch smoothing on mobile
      touchMultiplier: 2,
      infinite: false,
      // Mid-scroll resize resets animatedScroll (sticky bars, videos, lazy media)
      autoResize: false,
    });

    // Expose globally if needed
    window.lenis = this.lenis;
    window.lenisManager = this;

    this.startRAF();
    this.addEventListeners();
    this.observeContentResize();
    this.scheduleSafeResize();
  }

  startRAF() {
    if (this.rafHandle) return;

    const raf = (time) => {
      if (!this.lenis) return;
      this.lenis.raf(time);
      this.rafHandle = requestAnimationFrame(raf);
    };

    this.rafHandle = requestAnimationFrame(raf);
  }

  addEventListeners() {
    if (!this.lenis) return;
    this.lenis.on('scroll', this.onScroll);
  }

  onScroll(e) {
    // Custom scroll effects (if needed)
  }

  /**
   * Debounce Lenis.resize until scroll velocity settles.
   * Calling resize() while Lenis is interpolating causes stutter ("attack").
   */
  scheduleSafeResize() {
    if (!this.lenis) return;

    clearTimeout(this._safeResizeTimer);
    this._safeResizeTimer = setTimeout(() => {
      if (!this.lenis) return;

      const velocity = Math.abs(this.lenis.velocity || 0);
      if (velocity > 0.05) {
        this.scheduleSafeResize();
        return;
      }

      this.lenis.resize();
    }, 160);
  }

  observeContentResize() {
    this._contentResizeObserver?.disconnect();
    this._contentResizeObserver = null;

    if (!this.lenis || typeof ResizeObserver === 'undefined') return;

    const content =
      this.lenis.options?.content ||
      document.documentElement;

    this._contentResizeObserver = new ResizeObserver(() => {
      this.scheduleSafeResize();
    });
    this._contentResizeObserver.observe(content);
  }

  // Called on any resize via ResizeManager
  handleResize() {
    const shouldBeEnabled = this.shouldEnable();

    // Case 1: should be enabled but currently off → init
    if (shouldBeEnabled && !this.lenis) {
      this.init();
      return;
    }

    // Case 2: should be disabled but currently on → disable
    if (!shouldBeEnabled && this.lenis) {
      this.disableLenisInstance();
      return;
    }

    // Case 3: still enabled → resize after scroll settles
    if (shouldBeEnabled && this.lenis) {
      this.scheduleSafeResize();
    }
  }

  // Internal cleanup (but keep resize observer alive)
  disableLenisInstance() {
    clearTimeout(this._safeResizeTimer);
    this._safeResizeTimer = null;

    this._contentResizeObserver?.disconnect();
    this._contentResizeObserver = null;

    if (this.rafHandle) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }

    if (this.lenis) {
      this.lenis.off('scroll', this.onScroll);
      this.lenis.destroy();
      this.lenis = null;
    }

    window.lenis = null;
  }

  // Full destroy (for Shopify designMode / section:load reset)
  destroy() {
    this.disableLenisInstance();

    // Stop listening to ResizeManager completely
    ResizeManager.unsubscribe(this.handleResize);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.lenisManager = new LenisSmoothScroll();
});

// Handle theme editor changes
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:unload', () => {
    forceUnlockScrollIfIdle();
  });

  document.addEventListener('shopify:section:load', () => {
    forceUnlockScrollIfIdle();
    window.lenisManager?.destroy();
    window.lenisManager = new LenisSmoothScroll();
    window.lenis?.start?.();
  });
}
